import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { ctosApi, queriesApi, waitlistApi, settingsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import {
  Upload,
  Map,
  Users,
  History,
  Settings,
  LogOut,
  Wifi,
  TrendingUp,
  TrendingDown,
  List,
  FileSpreadsheet,
  ChevronRight,
  Check,
  X,
  Clock,
  Menu,
  X as CloseIcon,
  MapPin,
  BarChart3,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

// CTO Icon
const ctoIcon = L.divIcon({
  className: "custom-cto-icon",
  html: `<div style="
    width: 20px;
    height: 20px;
    background: #FF4500;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(255,69,0,0.4);
  "></div>`,
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

const navItems = [
  { icon: Map, label: "Dashboard", path: "/" },
  { icon: BarChart3, label: "Inteligência", path: "/inteligencia" },
  { icon: MapPin, label: "Mapa Demanda", path: "/mapa-demanda" },
  { icon: History, label: "Histórico", path: "/history" },
  { icon: List, label: "Fila de Espera", path: "/waitlist" },
  { icon: Users, label: "Usuários", path: "/users" },
  { icon: Settings, label: "Configurações", path: "/settings" },
];

export default function NOCDashboard() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [stats, setStats] = useState(null);
  const [ctos, setCtos] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dragActive, setDragActive] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [lastKmzUpdate, setLastKmzUpdate] = useState(null);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [statsRes, ctosRes, queriesRes, uploadsRes, lastUpdateRes] = await Promise.all([
          queriesApi.getStats(),
          ctosApi.getAll(),
          queriesApi.getAll({ limit: 10 }),
          ctosApi.getUploads(),
          ctosApi.getLastUpdate(),
        ]);

        setStats(statsRes.data);
        setCtos(ctosRes.data);
        setRecentQueries(queriesRes.data);
        setUploads(uploadsRes.data);
        if (lastUpdateRes.data?.uploaded_at) {
          setLastKmzUpdate(lastUpdateRes.data.uploaded_at);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Erro ao carregar dados");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Handle file upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    if (!file.name.endsWith(".kmz") && !file.name.endsWith(".kml")) {
      toast.error("Apenas arquivos KMZ ou KML são aceitos");
      return;
    }

    setUploading(true);
    try {
      const response = await ctosApi.upload(file);
      toast.success(response.data.message);
      if (response.data.leads_checked > 0) {
        toast.info(
          `Fila reprocessada: ${response.data.leads_newly_viable} novo(s) lead(s) viável(is) agora — total ${response.data.leads_viable_total} de ${response.data.leads_checked}`,
          { duration: 8000 }
        );
      }
      // Reload data
      const [ctosRes, uploadsRes, statsRes] = await Promise.all([
        ctosApi.getAll(),
        ctosApi.getUploads(),
        queriesApi.getStats(),
      ]);
      setCtos(ctosRes.data);
      setUploads(uploadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao fazer upload");
    } finally {
      setUploading(false);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  // Export waitlist
  const handleExportWaitlist = async () => {
    try {
      const response = await waitlistApi.export();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `fila_espera_${new Date().toISOString().split("T")[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Exportação concluída!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  // Calculate map center
  const mapCenter = ctos.length > 0
    ? [ctos[0].latitude, ctos[0].longitude]
    : [-15.7801, -47.9292];

  return (
    <div className="min-h-screen bg-slate-950 flex" data-testid="noc-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 p-4">
        <div className="flex items-center justify-between">
          <img 
            src="/tecplus-logo.png" 
            alt="Tecplus Telecom" 
            className="h-10 object-contain"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white"
          >
            {sidebarOpen ? <CloseIcon className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:relative inset-y-0 left-0 z-50 w-64 bg-slate-900 border-r border-slate-800 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-slate-800">
          <img 
            src="/tecplus-logo.png" 
            alt="Tecplus Telecom" 
            className="h-12 object-contain"
          />
          <p className="text-xs text-slate-400 mt-2">NOC Admin</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  isActive
                    ? "bg-[#FF4500] text-white"
                    : "text-slate-400 hover:bg-slate-800 hover:text-white"
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FF4500] flex items-center justify-center text-white font-bold">
              {user?.name?.charAt(0) || "A"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-slate-400">{user?.role?.toUpperCase()}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-7xl mx-auto space-y-6 lg:space-y-8">
          {/* Header */}
          <div>
            <h2 className="text-2xl lg:text-3xl font-bold text-white">Dashboard</h2>
            <p className="text-slate-400 mt-1">Visão geral do sistema de viabilidade</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-6">
            <Card className="bg-slate-900 border-slate-800 border-l-4 border-l-[#FF4500]">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-slate-400">Total Consultas</p>
                    <p className="text-xl lg:text-3xl font-bold text-white font-mono" data-testid="stat-total">
                      {stats?.total_queries || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                    <History className="w-5 h-5 lg:w-6 lg:h-6 text-[#FF4500]" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800 border-l-4 border-l-green-500">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-slate-400">Viáveis</p>
                    <p className="text-xl lg:text-3xl font-bold text-white font-mono" data-testid="stat-viable">
                      {stats?.viable_count || 0}
                    </p>
                    <p className="text-xs text-green-500 flex items-center gap-1 mt-1">
                      <TrendingUp className="w-3 h-3" />
                      {stats?.viability_rate || 0}%
                    </p>
                  </div>
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                    <Check className="w-5 h-5 lg:w-6 lg:h-6 text-green-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800 border-l-4 border-l-red-500">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-slate-400">Não Viáveis</p>
                    <p className="text-xl lg:text-3xl font-bold text-white font-mono" data-testid="stat-not-viable">
                      {stats?.not_viable_count || 0}
                    </p>
                    <p className="text-xs text-red-500 flex items-center gap-1 mt-1">
                      <TrendingDown className="w-3 h-3" />
                      {(100 - (stats?.viability_rate || 0)).toFixed(1)}%
                    </p>
                  </div>
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                    <X className="w-5 h-5 lg:w-6 lg:h-6 text-red-500" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900 border-slate-800 border-l-4 border-l-amber-500">
              <CardContent className="p-4 lg:p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs lg:text-sm text-slate-400">Fila de Espera</p>
                    <p className="text-xl lg:text-3xl font-bold text-white font-mono" data-testid="stat-waitlist">
                      {stats?.waitlist_count || 0}
                    </p>
                  </div>
                  <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                    <Clock className="w-5 h-5 lg:w-6 lg:h-6 text-amber-500" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {/* Upload KMZ */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-base lg:text-lg text-white flex items-center gap-2">
                  <Upload className="w-5 h-5 text-[#FF4500]" />
                  Upload KMZ
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className={`border-2 border-dashed rounded-lg p-4 lg:p-8 text-center transition-colors cursor-pointer ${
                    dragActive
                      ? "border-[#FF4500] bg-[#FF4500]/10"
                      : "border-slate-700 hover:border-slate-600"
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="kmz-dropzone"
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".kmz,.kml"
                    className="hidden"
                    data-testid="kmz-file-input"
                  />
                  <Upload
                    className={`w-10 h-10 mx-auto mb-3 ${
                      dragActive ? "text-[#FF4500]" : "text-slate-500"
                    }`}
                  />
                  <p className="text-sm text-slate-400 mb-2">
                    {uploading ? "Enviando..." : "Arraste o arquivo KMZ ou clique aqui"}
                  </p>
                  <p className="text-xs text-slate-500">Aceita arquivos .kmz ou .kml</p>
                </div>

                {/* Recent Uploads */}
                {uploads.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-slate-400 uppercase tracking-wide">
                      Últimos uploads
                    </p>
                    {uploads.slice(0, 3).map((upload, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-slate-800 rounded"
                      >
                        <div>
                          <p className="text-sm text-white truncate max-w-[150px]">
                            {upload.filename}
                          </p>
                          <p className="text-xs text-slate-500">
                            {upload.cto_count} CTOs
                          </p>
                          {upload.leads_checked !== undefined && (
                            <p className="text-xs text-green-400" data-testid={`upload-viable-leads-${index}`}>
                              +{upload.leads_newly_viable} leads viáveis ({upload.leads_viable_total}/{upload.leads_checked})
                            </p>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 font-mono">
                          {new Date(upload.uploaded_at).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-4 p-3 bg-slate-800 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-400">CTOs ativas:</span>
                    <span className="text-lg font-bold font-mono text-[#FF4500]" data-testid="cto-count">
                      {stats?.cto_count || ctos.length}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Map Preview */}
            <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <Map className="w-5 h-5 text-[#FF4500]" />
                  Mapa de CTOs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 rounded-lg overflow-hidden">
                  <MapContainer
                    center={mapCenter}
                    zoom={12}
                    className="h-full w-full"
                    zoomControl={false}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {ctos.map((cto) => (
                      <Marker
                        key={cto.id}
                        position={[cto.latitude, cto.longitude]}
                        icon={ctoIcon}
                      >
                        <Popup>
                          <span className="font-semibold">{cto.name}</span>
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Queries & Actions */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Queries */}
            <Card className="bg-slate-900 border-slate-800 lg:col-span-2">
              <CardHeader className="pb-4 flex flex-row items-center justify-between">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <History className="w-5 h-5 text-[#FF4500]" />
                  Consultas Recentes
                </CardTitle>
                <Link to="/history">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-slate-400 hover:text-white"
                  >
                    Ver todas
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {recentQueries.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">
                      Nenhuma consulta realizada ainda
                    </p>
                  ) : (
                    recentQueries.map((query) => (
                      <div
                        key={query.id}
                        className="flex items-center justify-between p-3 bg-slate-800 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              query.is_viable ? "bg-green-500/20" : "bg-red-500/20"
                            }`}
                          >
                            {query.is_viable ? (
                              <Check className="w-4 h-4 text-green-500" />
                            ) : (
                              <X className="w-4 h-4 text-red-500" />
                            )}
                          </div>
                          <div>
                            <p className="text-sm text-white truncate max-w-[300px]">
                              {query.address}
                            </p>
                            <p className="text-xs text-slate-500">
                              {query.user_name} • {query.distance_meters?.toFixed(0)}m
                            </p>
                          </div>
                        </div>
                        <span className="text-xs text-slate-500 font-mono">
                          {new Date(query.created_at).toLocaleDateString("pt-BR")}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-white">Ações Rápidas</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button
                  onClick={handleExportWaitlist}
                  variant="outline"
                  className="w-full justify-start gap-3 h-12 bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                  data-testid="export-waitlist-button"
                >
                  <FileSpreadsheet className="w-5 h-5 text-green-500" />
                  Exportar Fila de Espera
                </Button>

                <Link to="/waitlist" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12 bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                  >
                    <List className="w-5 h-5 text-amber-500" />
                    Ver Fila de Espera ({stats?.waitlist_count || 0})
                  </Button>
                </Link>

                <Link to="/users" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12 bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                  >
                    <Users className="w-5 h-5 text-blue-500" />
                    Gerenciar Usuários
                  </Button>
                </Link>

                <Link to="/settings" className="block">
                  <Button
                    variant="outline"
                    className="w-full justify-start gap-3 h-12 bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                  >
                    <Settings className="w-5 h-5 text-slate-400" />
                    Configurações
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer - Última atualização do mapa */}
        <div className="mt-6 pb-4 border-t border-slate-800 pt-4">
          <p className="text-center text-sm text-slate-400">
            Última atualização do mapa:{" "}
            <span className="text-green-400 font-semibold">
              {lastKmzUpdate 
                ? new Date(lastKmzUpdate).toLocaleDateString("pt-BR")
                : "--/--/----"
              }
            </span>
          </p>
        </div>
      </main>
    </div>
  );
}
